import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { noop } from 'lodash';

import { IconButton, Icon } from 'components';
import Modal from 'components/Modal';
import { Checkbox } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { OptionSelect } from 'components/filter';
import { FilterableFieldSetting, FilterableFieldTypes } from 'hooks/useGlobalSearch/useGlobalSearchInternal';
import { useResponsive } from 'hooks/useResponsive';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { useGlobalSearchData } from 'lib/providers/GlobalSearchDataProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './SearchTypeFilters.module.scss';

export const SearchTypeFilters: React.FC = () => {
  const router = useRouter();
  const { query } = router;
  const screens = useResponsive();
  const [tc] = useTranslation('common');
  const {
    isSearchModalOpen,
    partialFilter,
    currentFilters,
    isFiltersApplied,
    isPartialFiltersApplied,
    closeSearchFilterModal,
    getFieldOptions,
    getFieldSubOptions,
    onPartialUpdate,
    onPartialFiltersUpdate,
    onApplyPartialFilter,
    onResetPartialFilters,
  } = useGlobalSearch();
  const { filteredRecordsCount, isFilteredRecordsLoading } = useGlobalSearchData();
  const [currentSelectedFilter, setCurrentSelectedFilter] = useState<FilterableFieldSetting | null>(null);

  useEffect(() => {
    if (!isSearchModalOpen) {
      setCurrentSelectedFilter(null);
      onResetPartialFilters();
    }
  }, [isSearchModalOpen]);

  const handleFilterItemClick = useCallback(
    (filter: FilterableFieldSetting, value?: boolean) => {
      if (filter.type === FilterableFieldTypes.TOGGLE) {
        onPartialUpdate({ filter, value: value ? 'true' : null });
        return;
      }
      setCurrentSelectedFilter(filter);
    },
    [setCurrentSelectedFilter, partialFilter, onPartialUpdate]
  );

  const resetCurrentSelectedFilter = useCallback(() => {
    setCurrentSelectedFilter(null);
  }, [setCurrentSelectedFilter]);

  const onChangeValue = useCallback(
    (value: string) => {
      if (!currentSelectedFilter) return;
      onPartialUpdate({ filter: currentSelectedFilter, value });
    },
    [currentSelectedFilter, onPartialUpdate]
  );

  const onChangeValues = useCallback(
    (values: string[]) => {
      if (!currentSelectedFilter) return;
      onPartialUpdate({ filter: currentSelectedFilter, value: values });
    },
    [currentSelectedFilter, onPartialUpdate]
  );

  const onChangeGroupValues = useCallback(
    ({ values, subValues }: { values: string[]; subValues: string[] }) => {
      if (!currentSelectedFilter) return;
      onPartialUpdate({ filter: currentSelectedFilter, value: values, subValue: subValues });
    },
    [currentSelectedFilter, onPartialUpdate]
  );

  const currentSelectedFilterValue = useMemo(() => {
    if (!currentSelectedFilter) return '';
    const partialValue = partialFilter[currentSelectedFilter.param];
    const queryValue = query[currentSelectedFilter.param];
    const value = typeof partialValue !== 'undefined' ? partialValue : queryValue;

    return value ? (Array.isArray(value) ? value[0] : value) : '';
  }, [partialFilter, currentSelectedFilter, query]);

  const currentSelectedFilterValues = useMemo(() => {
    if (!currentSelectedFilter) return [];
    const partialValue = partialFilter[currentSelectedFilter.param];
    const queryValue = query[currentSelectedFilter.param];
    const value = typeof partialValue !== 'undefined' ? partialValue : queryValue;
    return value ? (Array.isArray(value) ? value : [value]) : [];
  }, [partialFilter, currentSelectedFilter, query]);

  const currentSelectedFilterSubValues = useMemo(() => {
    if (!currentSelectedFilter || !currentSelectedFilter.subParam) return [];
    const partialSubValue = partialFilter[currentSelectedFilter.subParam];
    const querySubValue = query[currentSelectedFilter.subParam];

    const value = typeof partialSubValue !== 'undefined' ? partialSubValue : querySubValue;

    return value ? (Array.isArray(value) ? value : [value]) : [];
  }, [partialFilter, currentSelectedFilter, query]);

  const selectedFilterValuesCount = useMemo(() => {
    if (!currentSelectedFilter) return 0;
    switch (currentSelectedFilter.type) {
      case FilterableFieldTypes.SINGLE_SELECT_TICK:
        return currentSelectedFilterValue ? 1 : 0;
      case FilterableFieldTypes.MULTI_SELECT_TICK:
      case FilterableFieldTypes.MULTI_SELECT_CHIP:
        return currentSelectedFilterValues.length;
      case FilterableFieldTypes.MULTI_SELECT_LOCATION:
        return currentSelectedFilterValues.length + currentSelectedFilterSubValues.length;
      default:
        return 0;
    }
  }, [currentSelectedFilter, currentSelectedFilterValue, currentSelectedFilterValues, currentSelectedFilterSubValues]);

  const hasSelectedFilterValue = selectedFilterValuesCount > 0;

  const fieldOptions = useMemo(() => (currentSelectedFilter ? getFieldOptions(currentSelectedFilter) : []), [currentSelectedFilter, getFieldOptions]);
  const fieldSubOptions = useMemo(
    () => (currentSelectedFilter ? getFieldSubOptions(currentSelectedFilter) : []),
    [currentSelectedFilter, getFieldSubOptions]
  );

  const handleShowResults = useCallback(() => {
    onApplyPartialFilter();
    setCurrentSelectedFilter(null);
    closeSearchFilterModal();
  }, [setCurrentSelectedFilter, onApplyPartialFilter, closeSearchFilterModal]);

  const handleResetCurrentFilter = useCallback(() => {
    if (!currentSelectedFilter) return;
    const value = currentSelectedFilter.type === FilterableFieldTypes.SINGLE_SELECT_TICK ? null : [];
    onPartialUpdate({ filter: currentSelectedFilter, value, subValue: currentSelectedFilter.subParam ? [] : undefined });
  }, [currentSelectedFilter, onPartialUpdate]);

  const handleClearAllPartialFilters = useCallback(() => {
    const newFilters: Record<string, null> = {};
    currentFilters.forEach((filter) => {
      newFilters[filter.param] = null;
      if (filter.subParam) {
        newFilters[filter.subParam] = null;
      }
    });
    onPartialFiltersUpdate(newFilters);
  }, [currentFilters]);

  const hasClearedAllPartialFilters = useMemo(() => {
    return currentFilters.every((filter) => {
      return partialFilter[filter.param] === null && (!filter.subParam || partialFilter[filter.subParam] === null);
    });
  }, [currentFilters, partialFilter]);

  const isResetAll = !currentSelectedFilter && (isFiltersApplied || isPartialFiltersApplied) && !hasClearedAllPartialFilters;

  const onFetchMoreData = useCallback(() => {
    if (currentSelectedFilter) {
      const filterData = currentFilters.find((filter) => filter.id === currentSelectedFilter.id);
      if (filterData?.fetchMoreData) filterData.fetchMoreData();
    }
  }, [currentFilters, currentSelectedFilter]);

  const onSearchData = useCallback(
    (searchText: string) => {
      if (currentSelectedFilter) {
        const filterData = currentFilters.find((filter) => filter.id === currentSelectedFilter.id);
        if (filterData?.onSearch) filterData.onSearch(searchText);
      }
    },
    [currentFilters, currentSelectedFilter]
  );

  const showRefLoader = useMemo(() => {
    if (currentSelectedFilter) {
      const data = currentFilters.find((filter) => filter.id === currentSelectedFilter.id);
      return data?.showLoader;
    }
  }, [currentFilters, currentSelectedFilter]);

  return (
    <Modal
      isOpen={isSearchModalOpen}
      onRequestClose={closeSearchFilterModal}
      closeOnOverlayClick={true}
      onOverlayClickClose={handleShowResults}
      className={styles['modal-container']}
      contentClassName={styles['modal-content']}
    >
      <header className={styles.header}>
        {currentSelectedFilter ? (
          <>
            <IconButton iconName="icon_chevron-left" size="small" onClick={resetCurrentSelectedFilter} className={styles['back-icon']} />
            <button className={styles.back} onClick={resetCurrentSelectedFilter}>
              <Icon iconName="icon_chevron-left" size="small" />
              <span>{tc('back')}</span>
            </button>
          </>
        ) : (
          isResetAll && (
            <button color="silent" onClick={handleClearAllPartialFilters} className={styles['clear-all']}>
              {tc('clear-all')}
            </button>
          )
        )}
        <p className={styles.title}>{currentSelectedFilter ? tc(currentSelectedFilter.labelKey) : tc('filter')}</p>
        <IconButton iconName="close" size="small" onClick={closeSearchFilterModal} className={styles.cancel} />
      </header>
      <main className={styles.main}>
        <div className={styles.content}>
          {currentSelectedFilter && (
            <div className={styles['filter-detail']}>
              {currentSelectedFilter.type === FilterableFieldTypes.SINGLE_SELECT_TICK && (
                <OptionSelect
                  value={currentSelectedFilterValue}
                  options={fieldOptions}
                  onChange={onChangeValue}
                  isSearchable={currentSelectedFilter.isSearchable}
                  placeholder={tc('search')}
                />
              )}
              {(currentSelectedFilter.type === FilterableFieldTypes.MULTI_SELECT_TICK ||
                currentSelectedFilter.type === FilterableFieldTypes.MULTI_SELECT_CHIP) && (
                <OptionSelect
                  values={currentSelectedFilterValues}
                  options={fieldOptions}
                  onChange={onChangeValues}
                  isSearchable={currentSelectedFilter.isSearchable}
                  placeholder={tc('search')}
                  chipOption={currentSelectedFilter.type === FilterableFieldTypes.MULTI_SELECT_CHIP}
                  onFetchMoreData={onFetchMoreData}
                  onSearch={onSearchData}
                  showRefLoader={showRefLoader}
                />
              )}
              {currentSelectedFilter.type === FilterableFieldTypes.MULTI_SELECT_LOCATION && (
                <OptionSelect
                  options={fieldOptions}
                  subOptions={fieldSubOptions}
                  values={currentSelectedFilterValues}
                  subValues={currentSelectedFilterSubValues}
                  onChange={onChangeGroupValues}
                  isSearchable={currentSelectedFilter.isSearchable}
                  placeholder={tc('search')}
                />
              )}
            </div>
          )}
          {!currentSelectedFilter && (
            <div className={styles['filter-list']}>
              {currentFilters.map((filter, index) => {
                const isToggle = filter.type === FilterableFieldTypes.TOGGLE;
                const fieldOptions = getFieldOptions(filter);
                const fieldSubOptions = getFieldSubOptions(filter);
                const paramValue = partialFilter[filter.param] as string;
                const paramSubValue = filter.subParam && (partialFilter[filter.subParam] as string);
                const value = typeof paramValue === 'undefined' ? query[filter.param] : paramValue;
                const subValue = typeof paramSubValue === 'undefined' ? filter.subParam && query[filter.subParam] : paramSubValue;
                const values = value ? (Array.isArray(value) ? value : [value]) : [];
                const subValues = subValue ? (Array.isArray(subValue) ? subValue : [subValue]) : [];

                const selectedOptionValueLabels = fieldOptions?.filter((option) => values.includes(option.value)).map((option) => option.label) || [];
                const selectedOptionSubValueLabels =
                  fieldSubOptions?.filter((option) => subValues.includes(option.value)).map((option) => option.label) || [];
                const labels = [...selectedOptionValueLabels, ...selectedOptionSubValueLabels].join(', ');

                return (
                  <Fragment key={filter.id}>
                    <div className={styles['filter-item']} onClick={() => handleFilterItemClick(filter, !(value === 'true'))}>
                      <div className={styles['filter-label-value']}>
                        <p className={styles['filter-label']}>{tc(filter.labelKey)}</p>
                        <p className={styles['filter-value']}>{labels}</p>
                      </div>
                      {isToggle ? <Checkbox checked={value === 'true'} onChange={noop} /> : <Icon iconName="icon_chevron-right" size="small" />}
                    </div>
                    {currentFilters.length - 1 !== index && <div className={styles.divider}></div>}
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <footer className={styles.footer}>
        {isResetAll && (
          <ButtonV3 color="secondary" onClick={handleClearAllPartialFilters} className={styles['reset-all']}>
            {tc('reset')}
          </ButtonV3>
        )}
        {hasSelectedFilterValue && (
          <ButtonV3
            color="secondary"
            onClick={handleResetCurrentFilter}
            className={styles.reset}
            isFullWidth={screens.sm}
            size={screens.sm ? 'large' : 'medium'}
          >
            {tc('reset')}
          </ButtonV3>
        )}
        {!currentSelectedFilter && !isResetAll && (
          <ButtonV3 color="secondary" onClick={closeSearchFilterModal} className={styles.cancel}>
            {tc('cancel')}
          </ButtonV3>
        )}
        <ButtonV3
          color="primary"
          isLoading={isFilteredRecordsLoading}
          onClick={handleShowResults}
          isFullWidth={screens.sm}
          size={screens.sm ? 'large' : 'medium'}
        >
          {tc('show')} {router.pathname === PAGE_ROUTES.CAREER_OPPORTUNITY ? null : filteredRecordsCount}
        </ButtonV3>
      </footer>
    </Modal>
  );
};

SearchTypeFilters.displayName = 'SearchTypeFilters';

export default SearchTypeFilters;
